/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { classNames } from '../utils/UNSAFE_classNames';
import { cssProps } from '@oracle/oraclejet-cssprops';
import { HTMLAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';

type IntrinsicProps = Pick<HTMLAttributesSignalExcluded<HTMLDivElement>, 'children'>;
type Props = {
  /**
   * Specifies the source for the image of the avatar.  Will only be displayed if no icon component is specified as a child.  Image will be rendered as a
   * background image.
   */
  src?: string;
  /**
   * Specifies the initials of the avatar.  Will only be displayed if both the src attribute and the child icon component are not specified.
   */
  initials?: string;
  /**
   * Specifies the background of the avatar.
   */
  background?:
    | 'neutral'
    | 'orange'
    | 'green'
    | 'teal'
    | 'blue'
    | 'slate'
    | 'pink'
    | 'purple'
    | 'lilac'
    | 'gray';

  /**
   * Specifies the size of the avatar.
   */
  size?: '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

  /**
   * Specifies the shape of the avatar. Can be square or circle.  The default value of this
   * property varies by theme.
   */
  shape?: 'square' | 'circle';
  /**
   * AccessibleLabel does not need to be set if associated information is available to assistive technologies elsewhere on the page,
   * for example if a person's name is shown next to the avatar.
   * Otherwise accessibleLabel must be set to make the page accessible.
   *
   *  If accessibleLabel  is set, role will internally be set to 'img'.
   */
  accessibleLabel?: string;
} & IntrinsicProps;

/**
 * An avatar represents a person or entity as initials or an image.
 */
export function Avatar({ children, src, ...otherProps }: Props) {
  if (src && !children) {
    return <ImageAvatar src={src} {...otherProps}></ImageAvatar>;
  } else {
    return <ContentAvatar {...otherProps}>{children}</ContentAvatar>;
  }
}

const ImageAvatar = ({ src, ...otherProps }: Props) => {
  const classes = classNames([contentStyles.base, contentStyles.image]);
  return (
    <AvatarWrapper {...otherProps}>
      <div class={classes} style={{ backgroundImage: `url(${src})` }}></div>
    </AvatarWrapper>
  );
};

const ContentAvatar = ({ children, initials, ...otherProps }: Props) => {
  const classes = [
    contentStyles.base,
    contentStyles.content,
    initials ? contentStyles.initials : contentStyles.icon,
    !initials && childrenStyles
  ];

  const childContent = initials || children;
  return (
    <AvatarWrapper {...otherProps}>
      <div aria-hidden="true" class={classNames(classes)}>
        {childContent}
      </div>
    </AvatarWrapper>
  );
};

const AvatarWrapper = ({
  background = 'neutral',
  size = 'md',
  shape = 'square',
  children,
  accessibleLabel
}: Props) => {
  const classes = classNames([
    styles.base,
    `oj-c-avatar-bg-${background}`,
    `oj-c-avatar-${size}`,
    shape === 'circle' && styles.circle
  ]);
  if (accessibleLabel) {
    return (
      <div aria-label={accessibleLabel} role="img" class={classes}>
        {children}
      </div>
    );
  } else {
    return <div class={classes}>{children}</div>;
  }
};

const styles = {
  base: cssProps`${{
    display: 'inline-grid',
    height: 'var(--oj-c-PRIVATE-DO-NOT-USE-avatar-size)',
    width: 'var(--oj-c-PRIVATE-DO-NOT-USE--avatar-size)'
  }}`,

  circle: cssProps`${{
    '--oj-c-PRIVATE-DO-NOT-USE-avatar-border-radius': '50%'
  }}`
};

const childrenStyles = cssProps`${{
  /* @ts-ignore */
  '& > *': {
    marginTop: '0 !important',
    marginBottom: '0 !important',
    marginLeft: '0 !important',
    marginRight: '0 !important'
  }
}}`;

const contentStyles = {
  base: cssProps`${{
    borderRadius: 'var(--oj-c-PRIVATE-DO-NOT-USE-avatar-border-radius)',
    alignSelf: 'stretch',
    alignItems: 'center',
    justifyContent: 'center',
    display: 'flex',
    height: 'var(--oj-c-PRIVATE-DO-NOT-USE-avatar-size)',
    width: 'var(--oj-c-PRIVATE-DO-NOT-USE-avatar-size)',
    gridRowStart: '1',
    gridColumnStart: '1',
    gridRowEnd: '2',
    gridColumnEnd: '2',
    backgroundPosition: 'center center',
    overflowX: 'hidden',
    overflowY: 'hidden'
  }}`,
  content: cssProps`${{
    backgroundColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-avatar-bg-color)',
    color: 'var(--oj-c-PRIVATE-DO-NOT-USE-avatar-text-color)',
    backgroundSize: '196px 196px',
    backgroundImage: 'var(--oj-c-PRIVATE-DO-NOT-USE-avatar-pattern)'
  }}`,
  image: cssProps`${{
    backgroundSize: 'cover'
  }}`,
  initials: cssProps`${{
    fontSize: 'var(--oj-c-PRIVATE-DO-NOT-USE-avatar-initials-font-size)',
    fontWeight: 'var(--oj-c-PRIVATE-DO-NOT-USE-avatar-initials-font-weight)'
  }}`,
  icon: cssProps`${{
    fontSize: 'var(--oj-c-PRIVATE-DO-NOT-USE-avatar-icon-font-size)'
  }}`
};
